/**
 * Root route gate for `/`.
 *
 * `/` is both the public marketing landing AND the redirect target
 * for magic-link / invite auth (Supabase PKCE returns to the site
 * root with `?code=…`). This component reconciles the two:
 *
 *   - session present            → /dashboard
 *   - auth callback in the URL    → spinner (PKCE exchange in flight;
 *     OR auth still loading          never flash the landing page or
 *                                    bounce to /login mid-exchange)
 *   - genuinely unauthenticated   → LandingPage
 *
 * Without this, an authenticated user returning from a magic link
 * lands on the static LandingPage and appears not to be signed in.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LandingPage } from '../pages/LandingPage';
import { authCallbackState } from '../lib/authCallback';

function Spinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-semibold text-primary">
          Signing you in…
        </span>
      </div>
    </div>
  );
}

export function RootRoute() {
  const { session, loading } = useAuth();
  const [callback] = useState(() => authCallbackState(window.location.search, window.location.hash));
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (callback !== 'pending' || session) return;
    const timer = setTimeout(() => setTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, [callback, session]);

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  if (callback === 'error' || timedOut) {
    // Replace the URL so rejected/consumed credentials do not remain in history.
    return <Navigate to="/login?auth_error=callback" replace />;
  }

  // PKCE code exchange runs during client init. Until it resolves,
  // keep showing the spinner so we neither flash the marketing page
  // nor decide "unauthenticated" prematurely.
  if (loading || callback === 'pending') {
    return <Spinner />;
  }

  return <LandingPage />;
}
